import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema, ServiceSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { useInView } from 'react-intersection-observer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, ArrowRight, MapPin, Clock, DollarSign, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

/* ─── Hero ─────────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Deck Services in New Jersey</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          Professional <span className="text-lime">Deck Services</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          Custom decks, repairs, replacements, composite installs, and full outdoor living spaces — built by licensed NJ craftsmen with 25+ years of experience.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Service Card ────────────────────────────────────── */
interface ServiceData {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  features: string[];
  priceRange?: string;
  timeframe?: string;
}

const ServiceCard = ({ service, index }: { service: ServiceData; index: number }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <article
      ref={ref}
      id={service.slug}
      itemScope
      itemType="https://schema.org/Service"
      className={`transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <div className="group relative bg-white border-2 border-navy/10 hover:border-lime transition-all duration-200 hover:-translate-y-1 hover:shadow-hard h-full flex flex-col">
        <div className="h-1.5 bg-lime" />

        <div className="p-6 md:p-7 flex flex-col flex-1">
          <div className="flex items-start justify-between mb-4">
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-lime">
              Service {String(index + 1).padStart(2, '0')}
            </span>
            <span className="font-display text-5xl font-bold text-navy/[0.07] leading-none select-none">
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>

          <h3
            itemProp="name"
            className="font-display text-lg font-bold text-navy mb-1 uppercase tracking-wide group-hover:text-lime transition-colors"
          >
            {service.title}
          </h3>
          <p className="text-xs font-display font-semibold text-lime uppercase tracking-[0.15em] mb-3">
            {service.tagline}
          </p>
          <p itemProp="description" className="text-stone text-sm leading-relaxed mb-5 font-body">
            {service.description}
          </p>

          {(service.priceRange || service.timeframe) && (
            <div className="flex flex-wrap gap-3 mb-5 text-xs">
              {service.priceRange && (
                <span className="flex items-center gap-1.5 bg-cream border border-navy/15 px-2.5 py-1 font-display font-bold text-navy uppercase tracking-wider">
                  <DollarSign className="w-3 h-3 text-lime" />
                  {service.priceRange}
                </span>
              )}
              {service.timeframe && (
                <span className="flex items-center gap-1.5 bg-cream border border-navy/15 px-2.5 py-1 font-display font-bold text-navy uppercase tracking-wider">
                  <Clock className="w-3 h-3 text-lime" />
                  {service.timeframe}
                </span>
              )}
            </div>
          )}

          <ul className="space-y-2 mb-5 flex-1">
            {service.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                <span className="text-navy/85 text-[13px] font-body leading-snug">{feature}</span>
              </li>
            ))}
          </ul>

          <Link to="/contact" className="mt-auto">
            <Button variant="cta" size="default" className="w-full h-10 text-xs">
              Get a Quote
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
};

/* ─── All Services Grid ──────────────────────────────── */
const servicesData: ServiceData[] = [
  {
    slug: 'custom-deck-design-build',
    title: 'Custom Deck Design & Build',
    tagline: 'From Sketch to Sawdust',
    description: 'Full-service deck design and construction — 3D renderings, engineering, permitting, and install by in-house craftsmen. Every deck is built to your vision and NJ code.',
    features: [
      '3D design renderings & material samples',
      'Structural engineering & permit handling',
      'Certified in-house installation team',
      'Lifetime workmanship warranty',
      'Multi-level & wrap-around layouts',
      'Custom railings, stairs & built-ins',
    ],
    priceRange: '$25–80/sq ft',
    timeframe: '5–10 Days',
  },
  {
    slug: 'deck-repair-restoration',
    title: 'Deck Repair & Restoration',
    tagline: 'Restore. Reinforce. Reseal.',
    description: 'Extend the life of your existing deck with expert structural repairs, board replacement, railing fixes, and professional staining — without the cost of full replacement.',
    features: [
      'Full safety & structural inspection',
      'Board & joist replacement (matched)',
      'Railing repair, tightening, upgrades',
      'Rot, mildew & water damage remediation',
      'Professional stain, seal, and refinish',
      'Same-day emergency repairs available',
    ],
    priceRange: '$500–5,000',
    timeframe: '1–3 Days',
  },
  {
    slug: 'deck-replacement',
    title: 'Complete Deck Replacement',
    tagline: 'Tear Out. Upgrade. Done.',
    description: 'When repair is not enough, we remove your old deck, upgrade the framing, and rebuild with modern materials, better drainage, and current NJ code compliance.',
    features: [
      'Full tear-down & debris haul-away',
      'Reinforced framing & ledger board',
      'Upgrade to composite / PVC / hardwood',
      'Improved drainage & ventilation',
      'Modern layouts & multi-level options',
      'Code-compliant railings & lighting',
    ],
    priceRange: '$8,000–45,000',
    timeframe: '4–8 Days',
  },
  {
    slug: 'composite-pvc-decking',
    title: 'Composite & PVC Decking',
    tagline: 'Low-Maintenance · Long Warranty',
    description: 'Certified installation of Trex, TimberTech, Azek, and Fiberon composite/PVC decking. Fade-resistant, splinter-free, and backed by 25–50 year manufacturer warranties.',
    features: [
      'Trex, TimberTech, Azek & Fiberon certified',
      'Hidden fastener (clip) systems',
      'Fade, stain & scratch resistant',
      'No sanding, staining, or sealing',
      'Cool-touch and premium textured boards',
      '25–50 year manufacturer warranties',
    ],
    priceRange: '$30–55/sq ft',
    timeframe: '4–7 Days',
  },
  {
    slug: 'wood-decking',
    title: 'Wood Decking Specialists',
    tagline: 'Cedar · Redwood · Ipe · PT',
    description: 'Classic pressure-treated, cedar, redwood, and exotic hardwood (Ipe, Cumaru, Tigerwood) decks — hand-selected lumber, tight spacing, and pro-grade finish.',
    features: [
      'Kiln-dried pressure-treated pine',
      'Western red cedar & clear redwood',
      'Exotic hardwoods (Ipe, Cumaru, Tigerwood)',
      'Pre-drilled & hidden-fastener options',
      'Oil-based staining & UV sealing',
      'Annual maintenance plans',
    ],
    priceRange: '$15–45/sq ft',
    timeframe: '3–7 Days',
  },
  {
    slug: 'outdoor-living',
    title: 'Outdoor Living Enhancements',
    tagline: 'Decks · Pergolas · Kitchens',
    description: 'Turn your deck into a full outdoor room — pergolas, railings, built-in seating, outdoor kitchens, low-voltage lighting, paver patios, and fire features.',
    features: [
      'Custom railings (wood, metal, glass, cable)',
      'Pergolas, gazebos & shade structures',
      'Paver patios & walkways',
      'Built-in benches, planters, storage',
      'Low-voltage LED deck & path lighting',
      'Outdoor kitchens & fire features',
    ],
    priceRange: '$2,000–30,000+',
    timeframe: '2–10 Days',
  },
  {
    slug: 'commercial-deck-services',
    title: 'Commercial Deck Services',
    tagline: 'Restaurants · Hotels · Multifamily',
    description: 'Heavy-use decks for restaurants, hotels, apartments, and HOAs. ADA-compliant designs, commercial-grade framing, and coordinated project scheduling that respects your hours.',
    features: [
      'ADA-compliant ramps & railings',
      'Heavy-load structural engineering',
      'Commercial-grade Trex & Ipe options',
      'Phased scheduling (minimize downtime)',
      'Insurance & liability certificates',
      'Annual maintenance contracts',
    ],
    priceRange: 'Custom Quote',
    timeframe: 'Project-based',
  },
];

const AllServices = () => (
  <section className="section-compact bg-cream">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Our Expertise</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Every Service Your <span className="text-lime">Deck Needs</span>
        </h2>
        <p className="text-stone text-base leading-relaxed font-body">
          Design, build, repair, or replace — one in-house team handles it all across New Jersey.
        </p>
      </div>

      <div itemScope itemType="https://schema.org/ItemList" className="max-w-[1500px] mx-auto">
        <Carousel
          opts={{ align: 'start', loop: true, slidesToScroll: 1 }}
          className="relative"
        >
          <CarouselContent className="-ml-4 py-3">
            {servicesData.map((service, index) => (
              <CarouselItem
                key={service.slug}
                className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                <ServiceCard service={service} index={index} />
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselPrevious
            className="hidden md:flex -left-3 lg:-left-6 h-12 w-12 rounded-none bg-navy border-2 border-navy text-lime hover:bg-lime hover:text-navy shadow-hard"
          >
            <ChevronLeft className="w-5 h-5" />
          </CarouselPrevious>
          <CarouselNext
            className="hidden md:flex -right-3 lg:-right-6 h-12 w-12 rounded-none bg-navy border-2 border-navy text-lime hover:bg-lime hover:text-navy shadow-hard"
          >
            <ChevronRight className="w-5 h-5" />
          </CarouselNext>
        </Carousel>

        <p className="text-center text-[11px] font-display uppercase tracking-[0.2em] text-stone/70 mt-6">
          ← Swipe or use arrows to browse all {servicesData.length} services →
        </p>
      </div>
    </div>
  </section>
);

/* ─── Process Section (AEO: step-by-step) ────────────── */
const ProcessSection = () => {
  const steps = [
    { n: '01', title: 'Free Consultation', desc: 'We visit your home, listen to your vision, and measure the space.' },
    { n: '02', title: 'Design & Quote', desc: '3D renderings, material samples, and a detailed transparent quote.' },
    { n: '03', title: 'Permits & Prep', desc: 'We handle permits, HOA paperwork, and ordering materials.' },
    { n: '04', title: 'Build & Finish', desc: 'In-house crew builds, finishes, inspects, and walks you through.' },
  ];

  return (
    <section className="section-compact bg-navy text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="section-eyebrow justify-center">How It Works</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
            Our 4-Step <span className="text-lime">Process</span>
          </h2>
          <p className="text-white/70 font-body">
            From first call to final nail — here's exactly what to expect.
          </p>
        </div>

        <ol
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto"
          itemScope
          itemType="https://schema.org/HowTo"
        >
          {steps.map((s) => (
            <li
              key={s.n}
              itemScope
              itemType="https://schema.org/HowToStep"
              className="relative bg-navy-deep border-2 border-white/10 hover:border-lime transition-colors p-6"
            >
              <div className="absolute -top-3 -left-3 bg-lime text-navy font-display font-bold text-lg w-10 h-10 flex items-center justify-center border-2 border-navy">
                {s.n}
              </div>
              <h3 itemProp="name" className="font-display text-base font-bold text-lime mb-2 uppercase tracking-wide mt-2">
                {s.title}
              </h3>
              <p itemProp="text" className="text-white/70 text-sm leading-relaxed font-body">
                {s.desc}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ─── FAQ Section (visible, AEO & GEO-optimized) ──────── */
const servicesFaqs = [
  {
    question: 'How much does a new deck cost in New Jersey?',
    answer: 'Deck costs in New Jersey typically range from $15–25 per square foot for pressure-treated wood, $30–45 per square foot for composite decking (Trex, TimberTech), and $50–80+ per square foot for premium hardwoods and custom designs. A standard 300 sq ft deck typically runs $6,000–$20,000 installed. DeckPros NJ provides transparent written estimates with no hidden fees.',
  },
  {
    question: 'How long does it take to build a deck?',
    answer: 'Most residential decks in NJ take 3–7 days to build. Simple deck replacements can finish in 2–3 days; complex multi-level designs with pergolas or outdoor kitchens can take 1–2 weeks. Weather and permit processing can extend timelines by 1–2 weeks before work starts.',
  },
  {
    question: 'Do I need a permit to build a deck in New Jersey?',
    answer: 'Yes — almost all decks in New Jersey require a building permit, especially decks attached to the home or higher than 30 inches above grade. DeckPros NJ handles all permits, HOA approvals, and inspections for every project.',
  },
  {
    question: 'What is the best decking material for New Jersey weather?',
    answer: 'Composite and PVC decking (Trex, TimberTech, Azek) are the best choice for NJ\'s freeze-thaw cycles and humid summers because they resist warping, rot, and fading. Pressure-treated pine is the most affordable option, while Ipe and cedar offer premium natural looks with proper maintenance.',
  },
  {
    question: 'Does DeckPros NJ offer deck repairs or only new builds?',
    answer: 'DeckPros NJ offers both. Our repair services include board replacement, structural repairs, railing fixes, staining/sealing, and rot remediation. If your existing deck is more than 20 years old or has significant framing damage, we can also provide a full replacement quote.',
  },
  {
    question: 'What areas of New Jersey do you serve?',
    answer: 'DeckPros NJ serves Morris County, Sussex County, Warren County, Hunterdon County, and Somerset County — plus surrounding towns across northern and central New Jersey. Call (201) 657-0855 to confirm service in your town.',
  },
  {
    question: 'Do you offer financing for deck projects?',
    answer: 'Yes. DeckPros NJ offers flexible financing options including 0% APR for 12 months and extended terms up to 84 months for qualified applicants. Financing is available for projects starting at $2,500.',
  },
  {
    question: 'What warranties do you provide on deck construction?',
    answer: 'DeckPros NJ provides a lifetime workmanship warranty on every deck we build. Materials are covered under manufacturer warranties — typically 25–50 years on composite/PVC decking and 10–25 years on premium wood.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream-dark">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Answers You Need</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Deck Service <span className="text-lime">Questions</span>
        </h2>
        <p className="text-stone font-body">
          Straight answers about cost, timing, materials, and permits in New Jersey.
        </p>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {servicesFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Local / Service Areas (Local SEO) ─────────────── */
const LocalSection = () => {
  const counties = [
    { name: 'Morris County', path: '/areas/morris-county' },
    { name: 'Sussex County', path: '/areas/sussex-county' },
    { name: 'Warren County', path: '/areas/warren-county' },
    { name: 'Hunterdon County', path: '/areas/hunterdon-county' },
    { name: 'Somerset County', path: '/areas/somerset-county' },
  ];

  return (
    <section className="section-snug bg-cream">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <p className="section-eyebrow justify-center">Where We Build</p>
        <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-3">
          Deck Builders Serving <span className="text-lime">New Jersey</span>
        </h2>
        <p className="text-stone font-body mb-6 max-w-xl mx-auto">
          We build decks across northern and central New Jersey — licensed, bonded, and insured in every county we serve.
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          {counties.map((c) => (
            <Link
              key={c.name}
              to={c.path}
              className="flex items-center gap-2 bg-white border-2 border-navy text-navy hover:bg-lime hover:text-navy hover:border-navy px-4 py-2.5 font-display uppercase text-xs font-bold tracking-[0.15em] transition-all"
            >
              <MapPin className="w-3 h-3" />
              {c.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Enhanced Services ItemList schema ─────────────── */
const ServicesItemListSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Deck Services by DeckPros NJ',
    description: 'Professional deck construction services in New Jersey.',
    numberOfItems: servicesData.length,
    itemListElement: servicesData.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Service',
        name: s.title,
        description: s.description,
        url: `https://deckprosnj.com/services#${s.slug}`,
        provider: {
          '@type': 'LocalBusiness',
          name: 'DeckPros NJ',
          telephone: '+1-201-657-0855',
        },
        areaServed: { '@type': 'State', name: 'New Jersey' },
      },
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Page ────────────────────────────────────────── */
const Services = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Deck Services in NJ"
        description="Custom deck design, repairs, replacement, composite (Trex/TimberTech) decking, wood decking, and outdoor living in Morris, Sussex, Warren, Hunterdon & Somerset NJ. Licensed & insured. Free quotes: (201) 657-0855."
        canonical="/services"
        keywords="deck services NJ, custom deck builder New Jersey, deck repair NJ, deck replacement NJ, composite decking installation, Trex installer NJ, TimberTech deck, wood deck builder NJ, outdoor living NJ, commercial deck contractor"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Services', url: '/services' },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={servicesFaqs} />
      <ServiceSchema
        name="Deck Construction, Repair & Outdoor Living Services"
        description="Full-service deck contracting in New Jersey — custom design, composite/PVC installs, wood decks, repairs, replacements, and outdoor living enhancements."
        url="/services"
      />
      <ServicesItemListSchema />

      <Header />
      <main>
        <PageHero />
        <AllServices />
        <ProcessSection />
        <FAQSection />
        <LocalSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default Services;
